import React from 'react';

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  size?: number;
}

type IconNode = React.ReactNode;

const ic = (children: IconNode, vb = '0 0 24 24') =>
  function Icon({ size = 20, strokeWidth = 2, style, ...rest }: IconProps) {
    return (
      <svg
        width={size} height={size} viewBox={vb} fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
        style={style} {...rest}
      >
        {children}
      </svg>
    );
  };

const P = (d: string) => <path d={d} key={d} />;
const Cr = (p: React.SVGProps<SVGCircleElement>) => <circle {...p} key={'c' + JSON.stringify(p)} />;
const Rc = (p: React.SVGProps<SVGRectElement>) => <rect {...p} key={'r' + JSON.stringify(p)} />;

export const Ico = {
  chevR: ic([P('m9 18 6-6-6-6')]), chevL: ic([P('m15 18-6-6 6-6')]), chevD: ic([P('m6 9 6 6 6-6')]),
  arrowR: ic([P('M5 12h14'), P('m12 5 7 7-7 7')]),
  plus: ic([P('M5 12h14'), P('M12 5v14')]), minus: ic([P('M5 12h14')]),
  check: ic([P('M20 6 9 17l-5-5')]),
  checkCircle: ic([P('M21.8 10A10 10 0 1 1 17 3.3'), P('m9 11 3 3L22 4')]),
  x: ic([P('M18 6 6 18'), P('M6 6l12 12')]),
  search: ic([Cr({ cx: 11, cy: 11, r: 8 }), P('m21 21-4.3-4.3')]),
  camera: ic([P('M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z'), Cr({ cx: 12, cy: 13, r: 3 })]),
  image: ic([Rc({ x: 3, y: 3, width: 18, height: 18, rx: 2 }), Cr({ cx: 9, cy: 9, r: 2 }), P('m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21')]),
  trash: ic([P('M3 6h18'), P('M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2')]),
  alert: ic([P('m21.7 18-8-14a2 2 0 0 0-3.5 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3Z'), P('M12 9v4'), P('M12 17h.01')]),
  info: ic([Cr({ cx: 12, cy: 12, r: 10 }), P('M12 16v-4'), P('M12 8h.01')]),
  pkg: ic([P('m7.5 4.3 9 5.1'), P('M21 8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z'), P('m3.3 7 8.7 5 8.7-5'), P('M12 22V12')]),
  receipt: ic([P('M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z'), P('M8 7h8'), P('M8 11h8'), P('M8 15h5')]),
  star: ic([P('M12 2.3l2.9 6 6.6.9-4.8 4.6 1.2 6.6L12 17.6 6.1 20.6l1.2-6.6L2.5 9.2l6.6-.9z')]),
  bolt: ic([P('M13 2 3 14h9l-1 8 10-12h-9l1-8Z')]),
  grid: ic([Rc({ x: 3, y: 3, width: 7, height: 7, rx: 1.5 }), Rc({ x: 14, y: 3, width: 7, height: 7, rx: 1.5 }), Rc({ x: 14, y: 14, width: 7, height: 7, rx: 1.5 }), Rc({ x: 3, y: 14, width: 7, height: 7, rx: 1.5 })]),
  book: ic([P('M4 19.5A2.5 2.5 0 0 1 6.5 17H20'), P('M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z')]),
  users: ic([P('M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2'), Cr({ cx: 9, cy: 7, r: 4 }), P('M22 21v-2a4 4 0 0 0-3-3.9'), P('M16 3.1A4 4 0 0 1 16 11')]),
  user: ic([P('M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2'), Cr({ cx: 12, cy: 7, r: 4 })]),
  store: ic([P('M4 2h16v5a2 2 0 0 1-4 0 2 2 0 0 1-4 0 2 2 0 0 1-4 0 2 2 0 0 1-4 0V2Z'), P('M5 7v13a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7'), P('M10 21v-6h4v6')]),
  phone: ic([P('M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.5-1.2a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2Z')]),
  clock: ic([Cr({ cx: 12, cy: 12, r: 10 }), P('M12 6v6l4 2')]),
  spark: ic([P('M9.9 15.5A2 2 0 0 0 8.5 14L2.4 12.5a.5.5 0 0 1 0-1L8.5 10A2 2 0 0 0 9.9 8.5L11.5 2.4a.5.5 0 0 1 1 0L14 8.5A2 2 0 0 0 15.5 10l6.1 1.5a.5.5 0 0 1 0 1L15.5 14a2 2 0 0 0-1.4 1.4l-1.6 6.1a.5.5 0 0 1-1 0z')]),
  logout: ic([P('M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4'), P('m16 17 5-5-5-5'), P('M21 12H9')]),
  bell: ic([P('M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9'), P('M10.3 21a1.9 1.9 0 0 0 3.4 0')]),
  sync: ic([P('M3 12a9 9 0 0 1 15-6.7L21 8'), P('M21 3v5h-5'), P('M21 12a9 9 0 0 1-15 6.7L3 16'), P('M3 21v-5h5')]),
} satisfies Record<string, (props: IconProps) => React.JSX.Element>;

export type IconName = keyof typeof Ico;
